import { apiAddInvoice } from "@lib/api/billing";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
import { Invoice, InvoiceBody } from "@lib/types/Billing";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "antd";
import useIntl from "react-intl/lib/src/components/useIntl";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import { IoMdSave } from "react-icons/io";
import { TiPlus } from "react-icons/ti";
import { FaCopy, FaDownload } from "react-icons/fa";
import { UseMutationProps } from "@lib/types/Api";
import { downloadPdf } from "@lib/helpers/FileHelper";
import { StyledInvoiceButtons } from "./index.styled";
import dayjs from "dayjs";
import { useApi } from "@lib/hooks/APIHooks";
import { useOrderQueries } from "@lib/apiQueries";

type Props = {
  invoice?: Invoice;
  setSelectedInvoice: (invoice: Invoice) => void;
  requestBody: InvoiceBody;
  invoiceListQueryKey: string[];
  handleCloneInvoice: () => void;
  updateQuery: UseMutationProps<Partial<Invoice>>;
  isEditMode: boolean;
};

export default function InvoiceSubmitButton(props: Props) {
  const {
    invoice,
    setSelectedInvoice,
    requestBody,
    invoiceListQueryKey,
    handleCloneInvoice,
    updateQuery,
    isEditMode,
  } = props;
  const { messages } = useIntl();
  const { queriesCacheClear } = useApi();
  const queryClient = useQueryClient();
  const { mutate: updateMutate, isPending: updateIsPending } = updateQuery;
  const { updateOrderMutation } = useOrderQueries({
    messages,
    queryKey: ["orders"],
  });
  const { mutate: addMutate, isPending: addIsPending } = useMutation({
    mutationFn: apiAddInvoice,
    mutationKey: [...invoiceListQueryKey],
    onSuccess(data) {
      setSelectedInvoice(data.data);

      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.invoice"] as string }}
        />,
      );
      queriesCacheClear({ search: "invoice" });
      queryClient.invalidateQueries({
        queryKey: [...invoiceListQueryKey],
      });
    },
  });

  const handleSubmit = async () => {
    if (!isEditMode) return;

    if (invoice?.id) {
      const { orders } = invoice;
      const { products } = requestBody;
      const needUpdate = orders
        .filter((order) => {
          const product = products.find((p) => p.id === order.productId);
          return (
            product?.unitPrice !== order.unitPrice ||
            product?.quantity !== order.quantity ||
            product?.discount !== order.discount
          );
        })
        .map((order) => {
          const product = products.find((p) => p.id === order.productId);
          return {
            id: order.id,
            unitPrice: product?.unitPrice,
            quantity: product?.quantity,
            discount: product?.discount,
          };
        });

      if (needUpdate.length > 0) {
        await Promise.all(
          needUpdate.map(async (order) => {
            await updateOrderMutation.mutateAsync({ ...order });
          }),
        );
      }

      return updateMutate(requestBody);
    }
    addMutate(requestBody);
  };

  return (
    <AppRowContainer gutter={2} style={{ marginBlock: 20 }}>
      <StyledInvoiceButtons className="invoice-buttons">
        {invoice?.id && isEditMode && (
          <Button
            color="orange"
            variant="solid"
            icon={<FaCopy />}
            onClick={() => {
              handleCloneInvoice();
            }}
          >
            {messages["common.clone"] as string}
          </Button>
        )}

        {invoice?.id && (
          <Button
            color="green"
            variant="solid"
            onClick={() => {
              downloadPdf({
                fileName: `${invoice?.customer?.name} - ${dayjs(invoice?.dueDate).format("MMMM")} - Invoice`,
              });
            }}
            icon={<FaDownload />}
          >
            {messages["common.downloadPdf"] as string}
          </Button>
        )}

        {isEditMode && (
          <Button
            color="blue"
            variant="solid"
            loading={addIsPending || updateIsPending}
            onClick={handleSubmit}
            icon={invoice?.id ? <IoMdSave /> : <TiPlus />}
          >
            {invoice?.id
              ? (messages["common.update"] as string)
              : (messages["common.create"] as string)}
          </Button>
        )}
      </StyledInvoiceButtons>
    </AppRowContainer>
  );
}
